import React, { createContext, useContext, useState, useReducer } from 'react';

const ReferralContext = createContext(null);

export const useReferral = () => {
    const context = useContext(ReferralContext);
    if (!context) {
        throw new Error('useReferral must be used within a ReferralProvider');
    }
    return context;
};

const initialState = {
    currentStep: 0,
    patient: {
        nhs_number: '',
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: '',
        address: '',
        postcode: '',
        phone: '',
        email: '',
        preferred_contact: 'phone'
    },
    patient_consent: {
        consent_given: false,
        consent_types: [],
        signatory_name: '',
        signature_date: null,
        digital_signature: null
    },
    bmi_data: {
        height_cm: null,
        height_feet: null,
        height_inches: null,
        weight_kg: null,
        weight_stones: null,
        weight_pounds: null
    },
    body_part: '',
    condition: '',
    medical_history: {
        diabetes: false,
        diabetes_type: null,
        hypertension: false,
        cardiac_disease: false,
        cardiac_details: '',
        respiratory_disease: false,
        respiratory_details: '',
        renal_disease: false,
        liver_disease: false,
        neurological_conditions: false,
        neurological_details: '',
        autoimmune_conditions: false,
        autoimmune_details: '',
        cancer_history: false,
        cancer_details: '',
        mental_health: false,
        mental_health_details: '',
        allergies: '',
        smoking_status: 'never',
        alcohol_units_per_week: 0,
        other_conditions: ''
    },
    surgical_history: {
        previous_surgeries: [],
        previous_joint_surgery: false,
        joint_surgery_details: '',
        previous_arthroscopy: false,
        arthroscopy_details: '',
        complications_history: ''
    },
    medications: {
        current_medications: [],
        anticoagulants: false,
        anticoagulant_name: '',
        steroids: false,
        immunosuppressants: false,
        nsaids_regular: false,
        opioids_regular: false
    },
    treatment_history: {
        physiotherapy: false,
        physio_sessions: 0,
        physio_benefit: '',
        regular_analgesia: false,
        analgesia_type: '',
        analgesia_duration_months: 0,
        intra_articular_injections: false,
        injection_type: '',
        injection_count: 0,
        last_injection_date: '',
        injection_benefit: '',
        walking_aids: false,
        walking_aid_type: '',
        weight_management: false,
        lifestyle_modifications: ''
    },
    questionnaire_scores: {
        oxford_hip_score: null,
        oxford_hip_answers: null,
        oxford_knee_score: null,
        oxford_knee_answers: null,
        ases_score: null,
        ases_answers: null,
        aofas_score: null,
        aofas_answers: null,
        odi_score: null,
        odi_answers: null,
        bss_red_flags: [],
        vas_pain_score: null
    },
    clinical_findings: {
        affected_side: '',
        symptom_duration_months: 0,
        pain_at_rest: false,
        night_pain: false,
        functional_limitation: 'moderate',
        xray_findings: '',
        mri_findings: '',
        other_imaging: '',
        examination_findings: ''
    },
    clinical_notes: '',
    urgency_justification: ''
};

function referralReducer(state, action) {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, currentStep: action.payload };
        case 'UPDATE_PATIENT':
            return { ...state, patient: { ...state.patient, ...action.payload } };
        case 'UPDATE_PATIENT_CONSENT':
            return { ...state, patient_consent: { ...state.patient_consent, ...action.payload } };
        case 'UPDATE_BMI_DATA':
            return { ...state, bmi_data: { ...state.bmi_data, ...action.payload } };
        case 'SET_BODY_PART':
            return { ...state, body_part: action.payload, condition: '' };
        case 'SET_CONDITION':
            return { ...state, condition: action.payload };
        case 'UPDATE_MEDICAL_HISTORY':
            return { ...state, medical_history: { ...state.medical_history, ...action.payload } };
        case 'UPDATE_SURGICAL_HISTORY':
            return { ...state, surgical_history: { ...state.surgical_history, ...action.payload } };
        case 'UPDATE_MEDICATIONS':
            return { ...state, medications: { ...state.medications, ...action.payload } };
        case 'UPDATE_TREATMENT_HISTORY':
            return { ...state, treatment_history: { ...state.treatment_history, ...action.payload } };
        case 'UPDATE_QUESTIONNAIRE_SCORES':
            return { ...state, questionnaire_scores: { ...state.questionnaire_scores, ...action.payload } };
        case 'UPDATE_CLINICAL_FINDINGS':
            return { ...state, clinical_findings: { ...state.clinical_findings, ...action.payload } };
        case 'UPDATE_NOTES':
            return { ...state, clinical_notes: action.payload.clinical_notes, urgency_justification: action.payload.urgency_justification };
        case 'APPLY_TEMPLATE':
            return {
                ...state,
                body_part: action.payload.body_part,
                condition: action.payload.condition,
                treatment_history: { ...state.treatment_history, ...(action.payload.pre_filled_data?.treatment_history || {}) },
                clinical_findings: { ...state.clinical_findings, ...(action.payload.pre_filled_data?.clinical_findings || {}) },
                questionnaire_scores: { ...state.questionnaire_scores, ...(action.payload.pre_filled_data?.questionnaire_scores || {}) }
            };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
}

export const ReferralProvider = ({ children }) => {
    const [state, dispatch] = useReducer(referralReducer, initialState);
    const [calculatedBMI, setCalculatedBMI] = useState(null);

    const nextStep = () => dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 });
    const prevStep = () => dispatch({ type: 'SET_STEP', payload: Math.max(0, state.currentStep - 1) });
    const goToStep = (step) => dispatch({ type: 'SET_STEP', payload: step });

    const updatePatient = (data) => dispatch({ type: 'UPDATE_PATIENT', payload: data });
    const updatePatientConsent = (data) => dispatch({ type: 'UPDATE_PATIENT_CONSENT', payload: data });
    const updateBMIData = (data) => dispatch({ type: 'UPDATE_BMI_DATA', payload: data });
    const setBodyPart = (part) => dispatch({ type: 'SET_BODY_PART', payload: part });
    const setCondition = (condition) => dispatch({ type: 'SET_CONDITION', payload: condition });
    const updateMedicalHistory = (data) => dispatch({ type: 'UPDATE_MEDICAL_HISTORY', payload: data });
    const updateSurgicalHistory = (data) => dispatch({ type: 'UPDATE_SURGICAL_HISTORY', payload: data });
    const updateMedications = (data) => dispatch({ type: 'UPDATE_MEDICATIONS', payload: data });
    const updateTreatmentHistory = (data) => dispatch({ type: 'UPDATE_TREATMENT_HISTORY', payload: data });
    const updateQuestionnaireScores = (data) => dispatch({ type: 'UPDATE_QUESTIONNAIRE_SCORES', payload: data });
    const updateClinicalFindings = (data) => dispatch({ type: 'UPDATE_CLINICAL_FINDINGS', payload: data });
    const updateNotes = (data) => dispatch({ type: 'UPDATE_NOTES', payload: data });
    const applyTemplate = (template) => dispatch({ type: 'APPLY_TEMPLATE', payload: template });
    const resetReferral = () => {
        dispatch({ type: 'RESET' });
        setCalculatedBMI(null);
    };

    const getReferralData = () => ({
        patient: state.patient,
        patient_consent: state.patient_consent,
        bmi_data: state.bmi_data,
        body_part: state.body_part,
        condition: state.condition,
        medical_history: state.medical_history,
        surgical_history: state.surgical_history,
        medications: state.medications,
        treatment_history: state.treatment_history,
        questionnaire_scores: state.questionnaire_scores,
        clinical_findings: state.clinical_findings,
        clinical_notes: state.clinical_notes,
        urgency_justification: state.urgency_justification
    });

    const value = {
        ...state,
        calculatedBMI,
        setCalculatedBMI,
        nextStep,
        prevStep,
        goToStep,
        updatePatient,
        updatePatientConsent,
        updateBMIData,
        setBodyPart,
        setCondition,
        updateMedicalHistory,
        updateSurgicalHistory,
        updateMedications,
        updateTreatmentHistory,
        updateQuestionnaireScores,
        updateClinicalFindings,
        updateNotes,
        applyTemplate,
        resetReferral,
        getReferralData
    };

    return (
        <ReferralContext.Provider value={value}>
            {children}
        </ReferralContext.Provider>
    );
};
